/**
 * A game controller plugged in or paired: an Xbox controller, or any the
 * browser gives the standard layout. Read each frame (`navigator.getGamepads`
 * has no events for buttons), and played into the game as its controls
 * (`core/input/gamepad.ts`): each button a key, pressed and let go as a key
 * is, on the window, so whatever listens for that key hears it; the sticks
 * and triggers into `PlayerController.pad`; the right stick turning the view
 * as the mouse does, or, while the player carries something, its up and
 * down raising and lowering it. It rumbles when the player crashes.
 *
 * A browser only shows a controller to a page once one of its buttons has
 * been pressed with the page in view.
 */

import type { PlayerMode } from '../config.js';
import { LOOK_SPEED, padFrame } from '../core/input/gamepad.js';
import type { PlayerController } from './PlayerController.js';

/** How fast the right stick raises or lowers what is held at full tilt, of its whole reach a second. */
const LIFT_RATE = 1.1;

export class GamepadControls {
  private held = new Set<string>();
  private index: number | undefined;
  /** What the player carries, if anything: the right stick's up and down raise and lower it (`raise` by a share of its reach). */
  carrying: { holding: () => boolean; raise: (by: number) => void } | undefined;

  /** `say` puts a line on the screen; `chime` sounds as one connects. */
  constructor(private readonly player: PlayerController, private readonly say: (text: string) => void, private readonly chime: () => void) {
    window.addEventListener('gamepadconnected', (e) => {
      if (this.index !== undefined) return;
      this.index = e.gamepad.index;
      this.say(`🎮 ${nameOf(e.gamepad.id)} connected`);
      this.chime();
    });
    window.addEventListener('gamepaddisconnected', (e) => {
      if (e.gamepad.index !== this.index) return;
      this.index = undefined;
      this.release();
      this.say('🎮 Controller disconnected');
    });
  }

  get connected(): boolean {
    return this.index !== undefined;
  }

  /** Each frame, before the player is moved: the controller as the controls, in `mode`. */
  update(dt: number, mode: PlayerMode): void {
    if (this.index === undefined) return;
    const pad = navigator.getGamepads?.()[this.index];
    if (pad === null || pad === undefined || !pad.connected) return;
    const f = padFrame(pad.buttons.map(b => b.value), pad.axes, mode);
    for (const code of f.keys) if (!this.held.has(code)) key('keydown', code);
    for (const code of this.held) if (!f.keys.has(code)) key('keyup', code);
    this.held = f.keys;
    const p = this.player.pad;
    p.moveX = f.moveX; p.moveY = f.moveY; p.throttle = f.throttle; p.steer = f.steer; p.yaw = f.yaw; p.collective = f.collective;
    // Slow near the middle for aiming, quick at full tilt.
    const curve = (v: number) => Math.sign(v) * v * v;
    let lookY = f.lookY;
    if (mode === 'walk' && this.carrying?.holding() === true) {
      // Up raises what is held, over the head in about a second; down lowers it.
      this.carrying.raise(-f.lookY * LIFT_RATE * dt);
      lookY = 0;
    }
    if (f.lookX !== 0 || lookY !== 0) this.player.lookBy(curve(f.lookX) * LOOK_SPEED * dt, curve(lookY) * LOOK_SPEED * dt);
  }

  /** A jolt through the hands: `strength` 0..1, for `ms`. */
  rumble(strength: number, ms: number): void {
    if (this.index === undefined) return;
    const pad = navigator.getGamepads?.()[this.index];
    const actuator = (pad as (Gamepad & { vibrationActuator?: { playEffect?: (type: string, params: object) => Promise<unknown> } }) | null)?.vibrationActuator;
    void actuator?.playEffect?.('dual-rumble', {
      duration: ms, strongMagnitude: Math.min(1, strength), weakMagnitude: Math.min(1, strength * 0.6),
    }).catch(() => undefined);
  }

  /** Let go of every key it holds, and the sticks back to the middle. */
  private release(): void {
    for (const code of this.held) key('keyup', code);
    this.held.clear();
    Object.assign(this.player.pad, { moveX: 0, moveY: 0, throttle: 0, steer: 0, yaw: 0, collective: 0 });
  }
}

function key(type: 'keydown' | 'keyup', code: string): void {
  window.dispatchEvent(new KeyboardEvent(type, { code, key: code, bubbles: true }));
}

/** "Xbox Wireless Controller (STANDARD GAMEPAD Vendor: 045e Product: 0b13)" as "Xbox Wireless Controller". */
function nameOf(id: string): string {
  const name = id.replace(/\s*\(.*$/, '').trim();
  return name === '' ? 'Controller' : name;
}
